import { useRef, useState } from "react"
import { Groups, MatchedStatuses, PriorityData, ProjectPage, useOutsideClick } from "utils"
import { RelatedProjects } from "utils/utils"
import ContactContent from "../../Contact/ContactContent"
import EligibilityForm from "../../Smp/EligibilityForm"
import ContactForm from "../Form"
import Partners from "../Partners"
import { ProjectsMessages } from "../Projects"
import { Backdrop, StickyCTAWrap, StickyFormWrap } from "../StandardProject"
import StickyCTA from "../StickyCTA"
import Apartments from "./Apartments"
import CTA from "./CTA"
import Description from "./Description"
import EntitledDocs from "./EntitledDocs"
import Gallery from "./Gallery"
import LegalNote from "./LegalNote"
import LotteryRegister from "./LotteryRegister"
import GoogleMap from "./Map"
import RelatedGallery from "./RelatedGallery"
import TopHeader from "./TopHeader"
import ApartmentsUmbraco from "./UmbracoApartments/Apartments"

type Props = {
  projectData: ProjectPage
  statuses: MatchedStatuses
  groups: Groups
  designations: string[]
  cities: string[]
  countries: string[]
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  relatedProjects?: RelatedProjects
  priorityData?: PriorityData
  toLobby?: string
  propertyTypes: {
    id: number
    value: string
  }[]
  useDrawerLayout?: boolean
  is4Rent?: boolean
  allRentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
}

function Marketing({
  projectData,
  locale,
  cities,
  countries,
  designations,
  groups,
  statuses,
  formSubjects,
  messages,
  relatedProjects,
  priorityData,
  toLobby,
  propertyTypes,
  useDrawerLayout,
  is4Rent,
  allRentalProjects,
  cmsProjectLeadIds,
}: Props) {
  const [isBackdropVisible, setIsBackdropVisible] = useState(false)
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const stickyRef = useRef(null)
  const formRef = useRef(null)
  const entitledProject = projectData.projectType === "זכאים"
  const handleBackdropClose = () => {
    setIsFormVisible(false)
    setIsBackdropVisible(false)
    setIsDropDownOpen(false)
  }
  useOutsideClick([stickyRef, formRef], handleBackdropClose)

  // This component also serves property and entitled projects, which route leads
  // through their own forms, so the empty state CTA is for apartment marketing
  // only. Projects that hide the lead form must not get one from the banner.
  const showEmptyStateLeadCta =
    projectData.projectType === "שיווק דירות" && !projectData.hideLeadForm
  const handleEmptyStateCta = () => {
    setIsBackdropVisible(true)
    setIsFormVisible(true)
  }
  const { gallery, contactAnchor } = projectData

  return (
    <>
      <TopHeader
        projectData={projectData}
        locale={locale}
        city={cities[0]}
        country={countries[0]}
        designations={designations}
        statuses={statuses}
        virtualTourLinkName={projectData.virtualTourLinkName}
        marketing
        contactAnchor={contactAnchor}
      />
      {/* {!!numbers && (
        <NumbersComponent
          data={numbers}
          textColor={sharedColors.white}
          backgroundColor={theme.colors.propBlue}
          accentColor={sharedColors.white}
          titleColor={sharedColors.white}
        />
      )} */}

      <Description projectData={projectData} locale={locale} toLobby={toLobby} />
      {priorityData ? (
        <Apartments
          projectData={projectData}
          priorityData={priorityData}
          locale={locale}
          formSubjects={formSubjects}
          messages={messages}
          allApChar={priorityData.allApChar}
          allBuChar={priorityData.allBuChar}
          companyId={projectData.companyId}
          useDrawerLayout={useDrawerLayout}
          is4Rent={is4Rent}
          projectFilterId={projectData.projectTitle}
          allRentalProjects={allRentalProjects}
          cmsProjectLeadIds={cmsProjectLeadIds}
          noAvailableApartmentsBanner={projectData.noAvailableApartmentsBanner}
          onEmptyStateCta={showEmptyStateLeadCta ? handleEmptyStateCta : undefined}
          leadFormEnabled={showEmptyStateLeadCta}
        />
      ) : null}
      <Gallery gallery={gallery} locale={locale} />
      {projectData.apartments.length > 0 ? (
        <ApartmentsUmbraco
          propertyTypes={propertyTypes}
          projectData={projectData}
          locale={locale}
          formSubjects={formSubjects}
          messages={messages}
        />
      ) : null}
      {!!projectData.lottery &&
      (projectData.lottery.boxTitle?.length || projectData.lottery.boxTextHTML?.length) ? (
        // eslint-disable-next-line react/jsx-pascal-case
        <CTA lottery={projectData.lottery} />
      ) : null}
      <GoogleMap address={projectData.address} locale={locale} />
      {entitledProject && <EntitledDocs docs={projectData.apartmentsGeneral.apartmentContracts} />}
      {entitledProject && (
        <LotteryRegister
          lotteryWays={projectData.lotteryWays}
          lotterRegisterTitle={projectData.lotterRegisterTitle}
        />
      )}
      {entitledProject && (
        <EligibilityForm
          data={projectData.formObject}
          companyId={projectData.companyId}
          projectId={projectData?.leadFormBMBYId}
          emailForLead={projectData?.emailForLead}
        />
      )}
      {projectData.officeInfo?.[0] ? (
        <ContactContent data={projectData.officeInfo?.[0]} project={true} asH2 />
      ) : null}

      <Partners
        messages={{
          groups: messages.groups,
          collaborators: projectData.groupPartnersTitle || "",
          entr: projectData.entrepreneursTitle || "",
        }}
        groups={groups}
        companyText={projectData.companyText}
        subTitle={projectData.subTitle2}
      />
      {isBackdropVisible && <Backdrop />}

      {!projectData.hideLeadForm && (
        <StickyCTAWrap ref={stickyRef} z={isFormVisible ? 90 : 100}>
          <StickyCTA
            projectType={projectData.projectType}
            messages={messages}
            setIsBackdropVisible={setIsBackdropVisible}
            open={isDropDownOpen}
            setIsFormVisible={setIsFormVisible}
            setIsDropDownOpen={setIsDropDownOpen}
            phone={projectData.contactPhone}
            miniSite={projectData.miniSite}
            virtualTourLink={projectData.virtualTourLink}
            virtualTourLinkName={projectData.virtualTourLinkName}
            hideLeadForm={!!projectData.hideLeadForm}
            isForMarketing
          />
        </StickyCTAWrap>
      )}

      {isFormVisible ? (
        <StickyFormWrap ref={formRef}>
          <ContactForm
            messages={messages}
            formSubjects={formSubjects}
            onClose={handleBackdropClose}
            bmbyId={projectData?.leadFormBMBYId}
            status={projectData?.projectStatuses[0]?.name}
            companyId={projectData?.companyId}
            emailForLead={projectData?.emailForLead}
          />
        </StickyFormWrap>
      ) : null}

      {relatedProjects && relatedProjects?.relatedProjects.length > 0 && (
        <RelatedGallery
          projectData={projectData}
          locale={locale}
          relatedObject={relatedProjects}
          toProject={messages.toProject}
        />
      )}
      <LegalNote text={projectData.legalNote || ""} />
    </>
  )
}

export default Marketing
